'use client'

import { useEffect, useCallback, useMemo, type ReactNode } from 'react'
import Image from 'next/image'
import Link from '@/components/ui/Link'
import * as m from 'motion/react-m'
import { useTransform, useScroll, useMotionValue, useSpring } from 'motion/react'
import { Motion } from '@/lib/motion/lazy-motion'
import Hero from '@/components/content/common/Hero'
import Container from '@/components/structure/Container'
import Award from '@/components/ui/Award'
import CriteriaIcons from '@/components/ui/CriteriaIcons'
import RankIndicator, { type RankChange } from '@/components/ui/RankIndicator'
import AppIcon from '@/components/content/app/AppIcon'
import EfficiencyPoints from '@/components/ui/EfficiencyPoints'
import Timestamp from '@/components/ui/Timestamp'
import { cn, hexToRgba, getBrandColors, isPopulatedObject } from '@/lib/utils'
import { useDeferredActivation } from '@/hooks/useDeferredActivation'
import { checkAppBranding } from '@/lib/formatting'
import { generateCriteriaList } from '@/components/content/comparisons/helpers'
import {
  Recommendation,
  Classification,
} from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import type { Timestamps } from '@/types/app'
import type {
  App,
  Media,
  Criterion,
  Review,
  Category,
  AppCategoryRecommendation,
  CategoryCriteriaCriterion,
  ReviewCategoryCriterionRating,
} from '@/payload-types'

type ReviewHeroProps = {
  app: Pick<
    App,
    | 'id'
    | 'icon'
    | 'slug'
    | 'glyph'
    | 'categoryRecommendations'
    | 'brandBackgroundColor'
    | 'brandPrimaryColor'
    | 'brandSecondaryColor'
    | 'brandTertiaryColor'
    | 'websiteUrl'
    | 'name'
    | 'screenshots'
    | 'homepageImage'
  >
  category?: Category & {
    recommendation: Recommendation
    classification: Classification
  }
  reviewId: Review['id']
  reviewName?: string
  reviewCriterionRatings?: Array<
    ReviewCategoryCriterionRating['id'] | ReviewCategoryCriterionRating
  >
  efficiencyPoints?: number
  root?: boolean
  timestamps?: Timestamps
  children?: ReactNode
  rank?: number
  rankChange?: RankChange
}

export default function ReviewHero({
  app,
  reviewName,
  category,
  reviewId,
  reviewCriterionRatings,
  efficiencyPoints,
  root,
  timestamps,
  children,
  rank,
  rankChange,
}: ReviewHeroProps) {
  const { primaryColor, secondaryColor, backgroundColor } = getBrandColors(app)
  const { appIcon, appGlyph, appHomepage } = checkAppBranding(app)

  const gradient = `radial-gradient(70% 100% at 0% 0%, ${hexToRgba(primaryColor, 0.25)} 0%, ${hexToRgba(primaryColor, 0)} 80%),
        radial-gradient(70% 100% at 100% 0%, ${hexToRgba(secondaryColor, 0.25)} 0%, ${hexToRgba(secondaryColor, 0)} 80%),
        radial-gradient(100% 100% at 50% 0%, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 80%, rgba(0, 0, 0, 0) 100%),
        #000`

  return (
    <Hero gradient={gradient} className="pt-[140px] md:pt-28" ftc={true}>
      {({ animations }) => (
        <Motion>
          <>
            <Container reduced={true} className="relative z-20">
              <m.div
                style={animations}
                className="flex flex-col items-center gap-6 sm:gap-7 md:items-start"
              >
                <ReviewHeroContent
                  app={app}
                  reviewName={reviewName}
                  reviewId={reviewId}
                  category={category}
                  reviewCriterionRatings={reviewCriterionRatings}
                  efficiencyPoints={efficiencyPoints}
                  root={root}
                  timestamps={timestamps}
                  rank={rank}
                  rankChange={rankChange}
                />

                {children && (
                  <div className="mt-1 flex flex-col items-center gap-3 sm:flex-row sm:gap-4">
                    {children}
                  </div>
                )}
              </m.div>
            </Container>

            {(appGlyph || appIcon) && (
              <Visual
                app={app.name}
                glyph={appGlyph}
                icon={appIcon}
                background={backgroundColor}
                screenshot={appHomepage?.url ?? undefined}
                priority
              />
            )}
          </>
        </Motion>
      )}
    </Hero>
  )
}

export function ReviewHeroContent({
  app,
  reviewName,
  category,
  reviewCriterionRatings,
  efficiencyPoints,
  root,
  timestamps,
  rank,
  rankChange,
}: ReviewHeroProps) {
  const displayName = reviewName?.trim() || app.name
  const populatedReviewCriterionRatings = useMemo(
    () =>
      reviewCriterionRatings?.filter((r): r is ReviewCategoryCriterionRating =>
        isPopulatedObject<ReviewCategoryCriterionRating>(r),
      ),
    [reviewCriterionRatings],
  )

  const appCriteria = useMemo(() => {
    const recommendations = (app.categoryRecommendations?.docs ?? []).filter(
      (r): r is AppCategoryRecommendation => isPopulatedObject<AppCategoryRecommendation>(r),
    )
    const filtered = category
      ? recommendations.filter(
          (r) => isPopulatedObject<Category>(r.category) && r.category.id === category.id,
        )
      : recommendations

    return filtered
      .map((r) => r.category)
      .filter((c): c is Category => isPopulatedObject<Category>(c))
      .flatMap((c) => (c.categoryCriteria?.docs ?? []) as CategoryCriteriaCriterion[])
      .filter((cc) => isPopulatedObject<CategoryCriteriaCriterion>(cc))
      .map((cc) => cc.criterion)
      .filter((crit): crit is Criterion => isPopulatedObject<Criterion>(crit))
  }, [app, category]) satisfies Criterion[]

  const hasRatings = efficiencyPoints !== undefined && efficiencyPoints !== null

  return (
    <>
      <div className="relative z-20 flex flex-col items-center gap-6 sm:flex-row sm:gap-10">
        {category && (
          <>
            <Link href={`/best/${category.slug}`}>
              <Award
                category={category.name}
                recommendation={category.recommendation}
                overlay={true}
                nested={true}
              />
            </Link>
            {!root && (
              <div className="flex items-center gap-5">
                {typeof rank === 'number' && (
                  <RankIndicator
                    rank={rank}
                    rankChange={rankChange}
                    theme="dark"
                    orientation="horizontal"
                    href={`/best/${category.slug}`}
                  />
                )}
                <CriteriaIcons
                  app={app}
                  categoryId={category.id}
                  reviewCriterionRatings={populatedReviewCriterionRatings}
                />
              </div>
            )}
          </>
        )}
      </div>

      <div
        className={cn(
          'flex flex-col gap-4 md:gap-6',
          root ? 'text-center' : 'text-center md:text-left',
        )}
      >
        <div className="flex flex-col items-center gap-3 md:items-start">
          {root && (
            <h1 className="w-full text-center font-sans text-12 font-medium uppercase tracking-widest text-white/40">
              Software Reviews
            </h1>
          )}
          {root ? (
            <span className="block font-heading text-36 font-bold md:text-64">{displayName}</span>
          ) : (
            <>
              <h1 className="font-heading text-36 md:text-64">{displayName}</h1>
              {timestamps && <Timestamp {...timestamps} lead="Updated" className="sm:hidden" />}
            </>
          )}
        </div>
        {hasRatings && (
          <div className="max-w-lg text-16 text-white/60 md:text-18">
            <div
              className={cn(
                'mx-auto flex w-fit flex-wrap items-center justify-center gap-1',
                !root ? 'md:mx-0' : '',
              )}
            >
              <span>Awarded</span>
              <EfficiencyPoints
                app={app}
                appCategoryEfficiencyPoints={efficiencyPoints}
                align="left"
                inline={true}
                categoryId={category?.id}
                reviewCriterionRatings={populatedReviewCriterionRatings}
              />
              <span>by editors.</span>
            </div>
            {appCriteria.length > 0 && <p>{generateCriteriaList(appCriteria)}</p>}
          </div>
        )}
      </div>
    </>
  )
}

// The hero visual is LCP-eligible (the screenshot/icon is usually the largest
// painted element on /apps/<slug>). Chrome ignores elements with `opacity: 0`
// for LCP, so the original motion fade-in pushed LCP past hydration.
// Render at the final state in SSR, then upgrade to interactive parallax after
// the shared deferred-activation gate.
export function Visual({
  app,
  glyph,
  icon,
  background,
  screenshot,
  priority,
}: {
  app: string
  glyph: Media | null
  icon: Media | null
  background: string
  screenshot?: string
  priority?: boolean
}) {
  const { ref, active: interactive } = useDeferredActivation({
    strategy: 'after-interaction-idle',
  })
  const height = 680
  const { scrollY } = useScroll()
  const springConfig = { damping: 20, stiffness: 200 }

  const logoX = useMotionValue(0)
  const logoY = useMotionValue(0)
  const logoSpringX = useSpring(logoX, springConfig)
  const logoSpringY = useSpring(logoY, springConfig)

  const appX = useMotionValue(0)
  const appY = useMotionValue(0)
  const appSpringX = useSpring(appX, springConfig)
  const appSpringY = useSpring(appY, springConfig)

  const screenX = useMotionValue(0)
  const screenY = useMotionValue(0)
  const screenSpringX = useSpring(screenX, springConfig)
  const screenSpringY = useSpring(screenY, springConfig)

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const { innerWidth, innerHeight } = window
      const { clientX, clientY } = e

      const relativeX = clientX - innerWidth / 2
      const relativeY = clientY - innerHeight / 2

      logoX.set(relativeX / 20)
      logoY.set(relativeY / 20)
      appX.set(relativeX / 40)
      appY.set(relativeY / 40)
      screenX.set(relativeX / 60)
      screenY.set(relativeY / 60)
    },
    [logoX, logoY, appX, appY, screenX, screenY],
  )

  useEffect(() => {
    if (!interactive) return
    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [interactive, handleMouseMove])

  const scrollGlyph = useTransform(scrollY, [0, height], [0, -50])
  const scrollApp = useTransform(scrollY, [0, height], [0, -100])
  const scrollScreen = useTransform(scrollY, [0, height], [0, -150])
  const opacity = useTransform(scrollY, [0, height], [1, 0])

  const appBackground = useMemo(
    () =>
      `radial-gradient(ellipse 25% 207% at 54.50% 43%, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 100%) ${background}`,
    [background],
  )

  const screenMask = useMemo(
    () => `radial-gradient(ellipse 65% 75% at 35% 25%, white 30%, rgba(255,255,255,0) 100%)`,
    [],
  )

  return (
    <m.div
      ref={ref}
      initial={false}
      style={interactive ? { opacity } : undefined}
      className={cn(
        'absolute z-20 aspect-screen max-h-[70%] w-[90%]',
        '-bottom-[10%] left-[5%]',
        'xs:-bottom-[15%] xs:left-[10%] xs:w-[75%]',
        'sm:-bottom-[20%]',
        'md:-bottom-0 md:left-1/2 md:w-[50vw] md:translate-x-0 md:tall:-bottom-0',
        'xl:-bottom-[15%] xl:tall:-bottom-[15%]',
        '2xl:w-[770px]',
      )}
    >
      <div className="relative h-full w-full scale-[1]">
        <m.div
          initial={false}
          className={cn(
            'absolute z-30 aspect-square w-[33%]',
            glyph?.isDark
              ? '-top-[10%] left-[25%] md:-top-[25%]'
              : '-top-[25%] left-[35%] md:-top-[40%]',
          )}
          style={
            interactive
              ? {
                  y: scrollGlyph,
                  x: logoSpringX,
                  translateY: logoSpringY,
                }
              : undefined
          }
        >
          <div className="backface-hidden absolute h-full w-full -rotate-[15deg] skew-x-12 skew-y-3 bg-cover bg-no-repeat">
            <AppIcon glyph={glyph} icon={icon} name={app} priority={priority} />
          </div>
        </m.div>

        <m.div
          initial={false}
          className="absolute -top-[10%] left-[25%] z-20 aspect-square w-[33%] md:-top-[25%]"
          style={
            interactive
              ? {
                  y: scrollApp,
                  x: appSpringX,
                  translateY: appSpringY,
                }
              : undefined
          }
        >
          <div
            className="squircle absolute h-full w-full -rotate-[15deg] skew-x-12 skew-y-3 transform-gpu bg-black shadow-[0px_50px_100px_-25px_rgba(0,0,0,1)]"
            style={{ background: appBackground }}
          ></div>
        </m.div>

        <m.div
          initial={false}
          className="absolute left-0 top-0 z-10 h-full w-full"
          style={
            interactive
              ? {
                  y: scrollScreen,
                  x: screenSpringX,
                  translateY: screenSpringY,
                }
              : undefined
          }
        >
          <div
            className="backface-hidden absolute left-[15%] h-full w-full -rotate-[15deg] skew-x-12 skew-y-3 overflow-hidden rounded-2xl bg-gray-950 shadow-[0px_50px_100px_-25px_rgba(0,0,0,1)] after:absolute after:left-0 after:top-0 after:h-full after:w-full after:rounded-2xl after:border after:border-white after:border-opacity-20"
            style={{
              WebkitMaskImage: screenMask,
              maskImage: screenMask,
            }}
          >
            {screenshot && (
              <Image
                src={screenshot}
                alt={`${app} screenshot`}
                fill
                priority={priority}
                sizes="(min-width: 1536px) 1080px, (min-width: 768px) 828px, 90vw"
                className="object-cover object-left-top"
              />
            )}
          </div>
        </m.div>
      </div>
    </m.div>
  )
}
